import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { apiClient } from '@/lib/apiClient'
import { API_ROUTES, PAGINATION } from '@/constants/apiRoutes'
import { CardManagementList, CardManagementSearchParams, CreateCardParam, CreatedCardResponse, EditCardParam } from '@/types/cardManagement'

export const getCardList = createAsyncThunk<CardManagementList, CardManagementSearchParams>(
    `${API_ROUTES.ADMIN_CARD_LIST_ENDPOINT}`,
    async ({ cert_format_family_id, family_name, note, page, page_size, display_order, cert_id }) => {
        const rawParams = {
            cert_format_family_id: cert_format_family_id,
            family_name: family_name,
            cert_id: cert_id,
            display_order: display_order,
            note: note,
            page: page,
            page_size: page_size
        }

        const params = new URLSearchParams()
        Object.entries(rawParams).forEach(([key, value]) => {
            if (value !== undefined) {
                params.append(key, String(value))
            }
        })

        const res = await apiClient.get<CardManagementList>(
            `${API_ROUTES.ADMIN_CARD_LIST_ENDPOINT}?${params.toString()}`
        );

        // Normalize payload (support apiClient wrapping)
        const payload = (res as any).data ?? res

        // client-side cert_id filter fallback
        if (cert_id && payload && Array.isArray(payload.items)) {
            const q = String(cert_id).toLowerCase();
            const filtered = payload.items.filter((item: any) => {
                const g = (item?.cert_id ?? '').toString().toLowerCase();
                return g.includes(q);
            });
            payload.items = filtered;
            payload.total = filtered.length;
        }

        return payload
    })

export const createCard = createAsyncThunk<CreatedCardResponse, CreateCardParam>(API_ROUTES.ADMIN_CARD_ENDPOINT, async (createCardParam) => {
    const res = await apiClient.post<CreatedCardResponse>(API_ROUTES.ADMIN_CARD_ENDPOINT, createCardParam);
    return res
})

export const deleteCard = createAsyncThunk<CreatedCardResponse, { cardId: string }>(API_ROUTES.ADMIN_CARD_ENDPOINT, async ({ cardId }) => {
    const res = await apiClient.delete<CreatedCardResponse>(`${API_ROUTES.ADMIN_CARD_ENDPOINT}/${cardId}`);
    return res;
})

export const editCard = createAsyncThunk<CreatedCardResponse, EditCardParam & { cert_format_family_id: string }>(API_ROUTES.ADMIN_CARD_ENDPOINT, async (editCardParam) => {
    const { cert_format_family_id, ...rest } = editCardParam
    const res = await apiClient.put<CreatedCardResponse>(`${API_ROUTES.ADMIN_CARD_ENDPOINT}/${cert_format_family_id}`, {
        ...rest
    });
    return res
})

const initialState: CardManagementList = {
    items: [],
    total: 0,
    page: PAGINATION.PAGE,
    page_size: PAGINATION.PAGE_SIZE,
    status: 'idle',
    error: null,
    message: '',
    loading: false
}

const cardManagementSlice = createSlice({
    name: 'cardManagement',
    initialState,
    reducers: {
        resetCardManagementState: (state) => {
            state.items = [];
            state.total = 0;
            state.page = PAGINATION.PAGE;
            state.page_size = PAGINATION.PAGE_SIZE;
            state.status = 'idle';
            state.error = null;
            state.message = '';
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getCardList.pending, (state) => {
                state.status = 'loading';
                state.loading = true;
                state.error = null;
            })
            .addCase(getCardList.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.items = action.payload.items;
                state.page = action.payload.page || PAGINATION.PAGE;
                state.page_size = action.payload.page_size || PAGINATION.PAGE_SIZE;
                state.total = action.payload.total || 0;
                state.message = action.payload.message || '';
                state.loading = false;
            })
            .addCase(getCardList.rejected, (state, action) => {
                state.status = 'error';
                state.error = action.payload as string || action.error.message || 'error';
                state.loading = false;
                state.items = [];
            });
    }
});

export default cardManagementSlice.reducer;
